import { AuditLogChangeType, HistoryDetailsItem } from "@common/types/auditLog";
import { HISTORY_FIELD_LABELS } from "@common/constants/history-source-types";
import {
    formatAuditLogRefValue,
    isHistoryField,
} from "../../utils/historyDetailsHelpers";
import {
    HistoryInvoiceTemplateChangeRow,
    HistoryInvoiceTemplateContentRow,
} from "../views/historyInvoiceTemplateDetailsViewConfig";

const FALLBACK_TEMPLATE_NAME = "Invoice template";
const CONTENT_UPDATED_LABEL = "Content updated";
const EMPTY_VALUE = "-";

function getFieldLabel(field: string): string {
    return isHistoryField(field) ? HISTORY_FIELD_LABELS[field] : field;
}

function getNonEmptyString(
    value: string | null | undefined,
): string | undefined {
    return value?.length ? value : undefined;
}

function getNameFromDiff(log: HistoryDetailsItem): string | undefined {
    const nameDiff = log.diff.find((diff) => diff.field === "name");
    if (!nameDiff) {
        return undefined;
    }
    const preferredRef =
        log.changeType === AuditLogChangeType.DELETE
            ? nameDiff.before
            : nameDiff.after;

    return getNonEmptyString(preferredRef.value);
}

export function resolveInvoiceTemplateDisplayName(
    log: HistoryDetailsItem,
): string {
    return (
        getNameFromDiff(log) ??
        getNonEmptyString(log.resource?.name) ??
        FALLBACK_TEMPLATE_NAME
    );
}

function buildBaseRow(log: HistoryDetailsItem) {
    return {
        id: log.id,
        resourceType: log.resourceType,
        resourceId: log.resourceId,
        templateName: resolveInvoiceTemplateDisplayName(log),
        logDate: log.createdAt,
        date: log.createdAt,
        entityId: log.entityId ?? null,
    };
}

export function buildInvoiceTemplateContentRow(
    log: HistoryDetailsItem,
): HistoryInvoiceTemplateContentRow {
    return { ...buildBaseRow(log), change: CONTENT_UPDATED_LABEL };
}

export function buildInvoiceTemplateChangeRow(
    log: HistoryDetailsItem,
): HistoryInvoiceTemplateChangeRow {
    const base = buildBaseRow(log);

    if (log.changeType === AuditLogChangeType.CREATE) {
        return {
            ...base,
            changedFieldLabel: null,
            previous: EMPTY_VALUE,
            modified: base.templateName,
        };
    }
    if (log.changeType === AuditLogChangeType.DELETE) {
        return {
            ...base,
            changedFieldLabel: null,
            previous: base.templateName,
            modified: EMPTY_VALUE,
        };
    }

    const [firstDiff] = log.diff;
    if (log.diff.length === 1) {
        return {
            ...base,
            changedFieldLabel: getFieldLabel(firstDiff.field),
            previous: formatAuditLogRefValue(firstDiff.before),
            modified: formatAuditLogRefValue(firstDiff.after),
        };
    }

    const joinSide = (side: "before" | "after") =>
        log.diff
            .map(
                (diff) =>
                    `${getFieldLabel(diff.field)}: ${formatAuditLogRefValue(diff[side])}`,
            )
            .join(" · ");

    return {
        ...base,
        changedFieldLabel: null,
        previous: joinSide("before"),
        modified: joinSide("after"),
    };
}
